<template>
  <div class="flex flex-1 flex-col lg:flex-row lg:overflow-x-hidden items-stretch">
    <Grid @api="getMeasurementPoints()" :payload="{results, count, next, prev}" class="w-full">
      <template #buttons>
        <base-button
          @click="openForm('create_point')"
          size="sm"
          class="w-full"
          :disabled="pendingFetchRequest"
          action="create_measuring_point"
        >
          <base-icon icon="measuring-point" class="text-lg" />
        </base-button>
      </template>

      <div class="flex flex-col sm:flex-row w-full overflow-x-hidden">
        <base-map
          class="flex"
          style="min-height: calc(100vh - 11rem)"
          :groups="[
            {list: mpWithoutMeter, label: 'without_meter'},
            {list: mpWithMeter, label: 'with_meter'},
          ]"
          v-model="thePoint"
          :is-editable="!pendingFetchRequest"
          :allow-new-marker="!pendingFetchRequest"
          :startPopulating="startPopulating"
        />
        <transition name="movefade">
          <div
            v-if="visibleAside"
            class="flex flex-col items-end space-y-4 lg:w-128 bg-secondary px-6 py-2 relative"
          >
            <base-button-icon icon="close" @click="closePanel()" />
            <base-card
              v-if="formType === 'create_point'"
              head="create_measuring_point"
              :subtitle="true"
            >
              <base-alert v-if="!thePoint.lat || !thePoint.lng" text="choose_point" type="info" />
              <form v-else @submit.prevent="createMeasuringPoint()">
                <FormError :errors="errors" class="text-sm" />
                <div class="flex justify-between flex-col mb-4 pb-6 border-b space-y-4">
                  <fieldset>
                    <base-input v-model="thePoint.name" label="name" class="flex-grow" :required="true" />
                  </fieldset>
                  <fieldset>
                    <label class="mb-1 block">
                      {{ $t(`mygwf.components.Label.company_name`) }} <span class="text-red-default">*</span>
                    </label>
                    <base-select
                      required
                      v-model="thePoint.company"
                      :options="$store.getters['companies/list']"
                      :optionType="{id: 'id', label: 'name'}"
                      allowClear
                      :placeholder="`${$store.getters['companies/list'].length} ${$t(
                        `mygwf.views.Companies.title`
                      )}`"
                    />
                  </fieldset>
                  <fieldset>
                    <base-input v-model="thePoint.mpid" label="id" class="flex-grow mt-2" />
                  </fieldset>
                  <fieldset>
                    <label class="mb-1 block">{{ $t(`mygwf.components.Label.meter`) }}</label>
                    <base-select
                      v-model="thePoint.meter_id"
                      :options="meterResults"
                      :optionType="{id: 'meter_id', label: 'meter_id'}"
                    />
                  </fieldset>
                  <div
                    class="flex-grow flex flex-col justify-between space-y-1 font-semibold whitespace-nowrap"
                  >
                    <div class="flex justify-between">
                      <span class="">{{ $t(`mygwf.components.Label.lat`) }}:</span>
                      <span class="font-medium">{{ thePoint.lat }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="">{{ $t(`mygwf.components.Label.long`) }}:</span>
                      <span class="font-medium">{{ thePoint.lng }}</span>
                    </div>
                  </div>
                </div>
                <span class="text-red-default">*</span> {{ $t('mygwf.components.Form.requiredField') }}
                <base-button
                  type="submit"
                  class="w-full mt-4"
                  :loading="isSavingPoint"
                  :disabled="$v.thePoint.$invalid"
                  action="submit"
                />
              </form>
            </base-card>
            <base-card
              v-if="formType === 'edit_point'"
              head="update_measuring_point"
              :subtitle="true"
            >
              <form @submit.prevent="updateMeasuringPoint()">
                <FormError :errors="errors" class="text-sm" />
                <div class="flex justify-between flex-col mb-4 pb-6 border-b space-y-4">
                  <base-input v-model="thePoint.name" label="name" class="flex-grow" :required="true" />
                  <fieldset>
                    <label class="mb-1 block">
                      {{ $t(`mygwf.components.Label.company_name`) }} <span class="text-red-default">*</span>
                    </label>
                    <base-select
                      required
                      v-model="thePoint.company"
                      :options="$store.getters['companies/list']"
                      :optionType="{id: 'id', label: 'name'}"
                      allowClear
                      :placeholder="`${$store.getters['companies/list'].length} ${$t(
                        `mygwf.views.Companies.title`
                      )}`"
                    />
                  </fieldset>
                  <fieldset>
                    <base-input v-model="thePoint.mpid" label="id" class="flex-grow mt-2" />
                  </fieldset>
                  <fieldset>
                    <label class="mb-1 block">{{ $t(`mygwf.components.Label.meter`) }}</label>
                    <base-select
                      v-model="thePoint.meter_id"
                      :options="meterResults"
                      :optionType="{id: 'meter_id', label: 'meter_id'}"
                    />
                  </fieldset>
                  <div
                    v-if="thePoint.lat && thePoint.lng"
                    class="flex-grow flex flex-col justify-between space-y-1 font-semibold whitespace-nowrap"
                  >
                    <div class="flex justify-between">
                      <span class="">{{ $t(`mygwf.components.Label.lat`) }}:</span>
                      <span class="font-medium">{{ thePoint.lat }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="">{{ $t(`mygwf.components.Label.long`) }}:</span>
                      <span class="font-medium">{{ thePoint.lng }}</span>
                    </div>
                  </div>
                </div>
                <span class="text-red-default">*</span> {{ $t('mygwf.components.Form.requiredField') }}
                <base-button
                  type="submit"
                  class="w-full mt-4"
                  :loading="isSavingPoint"
                  :disabled="$v.thePoint.$invalid"
                  action="update"
                />
              </form>
            </base-card>
          </div>
        </transition>
      </div>
    </Grid>
  </div>
</template>

<script>
import {
  BaseAlert,
  BaseButton,
  BaseButtonIcon,
  BaseCard,
  BaseInput,
  BaseIcon,
  BaseMap,
  BaseSelect,
} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'
import isEmpty from 'lodash-es/isEmpty'
import FormError from '@/components/shared/FormError.vue'
import { required, numeric } from "vuelidate/lib/validators";

export default {
  mounted() {
    this.getMeters()
  },
  methods: {

    rendersMarkersOnMap() {
      this.startPopulating = false;
      this.$nextTick(() => this.startPopulating = true)
    },

    async getMeasurementPoints() {
      this.startPopulating = false
      try {
        this.measuringPoints = await this.$store.dispatch('measuringPoints/get')
        this.startPopulating = true
      } catch (error) {}
    },
    getMeters() {
      this.$store.dispatch('devices/getAfm').then(data => (this.meters = data.results))
    },
    createMeasuringPoint() {
      if (!this.thePoint.lat || !this.thePoint.lng) {
        this.errors = {error: ['no_coordinates']}
        return false
      }

      this.isSavingPoint = true

      let payload = {
        name: this.thePoint.name,
        mpid: this.thePoint.mpid,
        lat: parseFloat(this.thePoint.lat).toFixed(6),
        lng: parseFloat(this.thePoint.lng).toFixed(6),
        company: this.pointCompany,
      }

      if (this.thePoint.meter_id) {
        payload = {...payload, meter_id: this.thePoint.meter_id}
      }
      this.$store
        .dispatch('measuringPoints/create', payload)
        .then(point => {
          this.errors = {}
          this.thePoint = {}
          this.$store.dispatch('app/notification', {type: 'success', group: 'app', text: `created`})
          this.measuringPoints.push(point)
          this.rendersMarkersOnMap()
        })
        .catch(errors => {
          this.errors = errors
        })
        .finally(() => {
          this.isSavingPoint = false
        })
    },
    updateMeasuringPoint() {
      this.isSavingPoint = true
      let payload = {
        name: this.thePoint.name,
        mpid: this.thePoint.mpid,
        lat: parseFloat(this.thePoint.lat).toFixed(6),
        lng: parseFloat(this.thePoint.lng).toFixed(6),
        company: this.pointCompany,
      }
      if (this.thePoint.meter_id) {
        payload = {...payload, meter_id: this.thePoint.meter_id}
      }
      this.$store
        .dispatch('measuringPoints/update', {id: this.thePoint.id, body: payload})
        .then(point => {
          this.errors = {}
          this.thePoint = {}
          this.$store.dispatch('app/notification', {type: 'success', group: 'app', text: `updated`})
          this.measuringPoints = this.measuringPoints.map(e => point.id === e.id ? point : e);
          this.rendersMarkersOnMap()
        })
        .catch(errors => {
          this.errors = errors
        })
        .finally(() => (this.isSavingPoint = false))
    },
    openForm(type) {
      this.thePoint = {}
      this.isPanelVisible = true
      this.form = type
    },
    closePanel() {
      this.thePoint = {}
      this.errors = {}
      this.isPanelVisible = false
    },
  },
  data() {
    return {
      serial: '',
      thePoint: {},
      newDevice: {},
      meter: null,
      isSavingDevice: false,
      isSavingPoint: false,
      company2: null,
      measuringPoints: [],
      meters: null,
      startPopulating: false,
      isPanelVisible: false,
      form: '',
      errors: {},
    }
  },
  computed: {
    mpWithMeter() {
      return this.measuringPoints.filter(point => point.meter_id)
    },
    mpWithoutMeter() {
      return this.measuringPoints.filter(point => !point.meter_id)
    },
    meterResults() {
      return this.meters ? this.meters : []
    },
    visibleAside() {
      return this.isPanelVisible || !isEmpty(this.thePoint)
    },
    deviceCompany: {
      get() {
        return this.newDevice.company
      },
      set(value) {
        return this.$store.getters['companies/list'].filter(c => c.id === value)[0]
      },
    },
    pointCompany: {
      get() {
        return this.thePoint.company
      },
      set(value) {
        return this.$store.getters['companies/list'].filter(c => c.id === value)[0]
      },
    },
    formType: {
      get() {
        this.errors = {}
        if (this.thePoint.mode === 'new') {
          return 'create_point'
        }
        if (this.thePoint.mode === 'edit') {
          return 'edit_point'
        }
        return this.form
      },
      set(value) {
        return value
      },
    },
    keyTypes() {
      return this.$store.getters['devices/moduleTypes'].filter(type => type.label === 'AFM')[0]
    },
    count() {
      return this.measuringPoints.length
    },
    next() {
      return null
    },
    prev() {
      return null
    },
    results() {
      return this.measuringPoints || []
    },
    pendingFetchRequest() {
      return this.$store.getters['app/isApiFetching'];
    }
  },
  components: {
    BaseAlert,
    BaseButton,
    BaseButtonIcon,
    BaseCard,
    BaseInput,
    BaseIcon,
    Grid,
    BaseMap,
    BaseSelect,
    FormError,
  },

  // ================================================================================
  // Validation
  // ================================================================================
  validations: {
    thePoint: {
      name: { required },
      company: { required, numeric },
      lat: { required },
      lng: { required }
    }
  }
}
</script>
